import { api_configuration } from "../../common";
import api_gui = api_configuration.libraries.gui_definition_lib.types.gui_model.alan_api;
import api_cs = api_configuration.types.client_state.alan_api;
import api_crep = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.client_reply.alan_api;
import api_creq = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.client_request.alan_api;
import { IdPathBuilder } from "../navigation/IdPathBuilder";
import NodePathBuilder from "../navigation/NodePathBuilder";
import { DatastoreProxy } from "../interface/DatastoreProxy";
import * as expressions from "../state/expressions";
import DestructorTree from "../DestructorTree";
import { createIdPathBuilderFromNode } from "../navigation/createIdPathFromNode";
import { registerIdPath } from "../state/registerClientState";
import { awaitCondition } from "../../../../../stream";
import * as Status from "../Status";

function visitAncestorNodePath(path: api_gui.Cancestor_node_path, id_path_builder: IdPathBuilder): IdPathBuilder {
	return path.properties.has_steps.switch({
		no: id_path_builder,
		yes: ($) => visitAncestorNodePath($.properties.tail, id_path_builder.removeTail()),
	});
}

function visitGuiStaticSingularPath(
	gui_static_singular_path: api_gui.Cgui_static_singular_path,
	id_path_builder: IdPathBuilder
): IdPathBuilder {
	const ancestor_builder = visitAncestorNodePath(gui_static_singular_path.properties.head, id_path_builder);
	return gui_static_singular_path.properties.has_steps.switch({
		no: ancestor_builder,
		yes: ($) =>
			visitGuiStaticSingularPath(
				$.properties.tail,
				$.properties.type.switch({
					group: (group) => ancestor_builder.group(group.properties.group.entry),
				})
			),
	});
}
function createEmptyNodePath() {
	return new NodePathBuilder().build();
}
type PropertyResult = api_crep.Cproperties__node_query_result | undefined;
function queryProperty(
	id_path_builder: IdPathBuilder,
	property: { [key: string]: api_creq.Tproperties__node_query }
): Promise<PropertyResult> {
	return DatastoreProxy.instance
		.collectionQuery({
			"context node path": id_path_builder.build(),
			"select entries": [
				"yes",
				{
					"limit number of entries": 1,
					properties: {
						properties: property,
					},
				},
			],
			"collection path": {
				"has steps": ["no", {}],
			},
			filters: {
				"todo filter": ["no", {}],
				"value filters": {},
			},
			aggregates: {},
		})
		.then((result) =>
			result.properties.context.switch({
				"does not exist": undefined,
				"permission denied": undefined,
				exists: (result) =>
					result.properties.entries.switch<PropertyResult>({
						yes: (has_entries) =>
							has_entries.properties.entries.properties.has_steps.switch({
								no: (no_step) => no_step.properties.properties.properties.properties.get(Object.keys(property)[0]),
								yes: () => {
									throw new Error("Collection step is not expected");
								},
							}),
						no: undefined,
					}),
			})
		);
}
function evaluateFileNameExpression(
	id_path_builder: IdPathBuilder,
	file_name_expression: api_gui.Cfile_name_expression
): Promise<string> {
	return file_name_expression.properties.has_steps.switch({
		no: () => Promise.resolve(""),
		yes: ($) =>
			$.properties.type
				.switch<Promise<string>>({
					static: ($) => Promise.resolve($.properties.text),
					property: function ($) {
						const id_path_builder_to_property = visitGuiStaticSingularPath($.properties.path, id_path_builder);
						return $.properties.type.switch({
							text: function ($) {
								const property_name = $.properties.text.entry;
								return queryProperty(id_path_builder_to_property, {
									[property_name]: {
										path: createEmptyNodePath(),
										type: [
											"text",
											{
												expression: {
													type: [
														"singular",
														{
															expression: {
																type: [
																	"dynamic",
																	{
																		path: {
																			"has steps": ["no", {}],
																		},
																		text: property_name,
																	},
																],
															},
														},
													],
												},
											},
										],
									},
								}).then((property) => {
									let property_value = "";
									if (property) {
										property.properties.result.switch({
											"does not exist": undefined,
											"permission denied": undefined,
											exists: ($) => {
												property_value = $.properties.type.cast("text").properties.value;
											},
										});
									}
									return property_value;
								});
							},
							number: function ($) {
								const property_name = $.properties.number.entry;
								return queryProperty(id_path_builder_to_property, {
									[property_name]: {
										path: createEmptyNodePath(),
										type: [
											"number",
											{
												expression: {
													type: [
														"number property",
														{
															"signed number property": {
																path: createEmptyNodePath(),
																sign: ["positive", {}],
																"property name": property_name,
															},
														},
													],
												},
											},
										],
									},
								}).then((property) => {
									let property_value = "";
									if (property) {
										property.properties.result.switch({
											"does not exist": undefined,
											"permission denied": undefined,
											exists: ($) => {
												property_value = "" + $.properties.type.cast("number").properties.value.value;
												//TODO numberical type conversion
											},
										});
									}
									return property_value;
								});
							},
							"state group": function (state_group_expression) {
								const property_name = state_group_expression.properties.state_group.entry;
								return queryProperty(id_path_builder_to_property, {
									[property_name]: {
										path: createEmptyNodePath(),
										type: [
											"state group",
											{
												"state group": property_name,
											},
										],
									},
								}).then((property) => {
									if (property) {
										return property.properties.result.switch({
											"does not exist": Promise.resolve("-"),
											"permission denied": Promise.resolve("-"),
											exists: ($) => {
												const state_name = $.properties.type.cast("state group").properties.state.entry;
												const state_file_name_expression =
													state_group_expression.properties.states.get(state_name)!.properties
														.state_file_name_expression;
												return evaluateFileNameExpression(
													id_path_builder_to_property.state(property_name, state_name),
													state_file_name_expression
												);
											},
										});
									}
									return Promise.resolve("");
								});
							},
						});
					},
				})
				.then((file_name_prefix) =>
					evaluateFileNameExpression(id_path_builder, $.properties.tail).then(
						(tail_value) => file_name_prefix + tail_value
					)
				),
	});
}

export default async function (
	file: api_gui.Cfile__type__property,
	context: api_cs.Cnode | IdPathBuilder,
	extension: string
): Promise<string> {
	let file_name = "";
	switch (file.properties.file_name_source.state.name) {
		case "expression": {
			const $ = file.properties.file_name_source.state.node;
			const destructor = new DestructorTree();
			let node: api_cs.Cnode;
			if (context instanceof IdPathBuilder) {
				node = registerIdPath(context, destructor);
			} else {
				node = context;
			}
			const expression = expressions.ui_scalar_default<expressions.StringLike>(
				$.inferences.expression().properties.file_name_expression
			)([node, null]);
			const last_value = await awaitCondition(expression, (value): value is expressions.StringLike =>
				Status.isResolved(value)
			);
			destructor.destroy();
			if (last_value === undefined) {
				file_name = "";
			} else {
				file_name = last_value.toString();
			}
			break;
		}
		case "generated": {
			const $ = file.properties.file_name_source.state.node;
			let id_path_builder: IdPathBuilder;
			if (context instanceof api_cs.Cnode) {
				id_path_builder = createIdPathBuilderFromNode(context);
			} else {
				id_path_builder = context;
			}
			file_name = await evaluateFileNameExpression(id_path_builder, $.properties.file_name_expression);
			break;
		}
		default:
			unexpectedState(file.properties.file_name_source.state);
	}
	return joinWithDot(file_name, extension);
}

function joinWithDot(file_name: string, extension: string) {
	if (file_name.endsWith(".") || extension.startsWith(".")) {
		return file_name + extension;
	} else if (extension.length > 0) {
		return file_name + "." + extension;
	} else {
		return file_name;
	}
}
